import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { DisplayModes } from 'components/DataTable/Types';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';
import { Button } from 'components/Form/Button/Button';

export const ErrorsNavigation = ({ mode, numberOfErrors }: { mode: DisplayModes; numberOfErrors: number }) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { service } = useErrorContext<WebEDIError>();
  const moveToNextWebEDIError = service.moveToNextError;
  const moveToPreviousWebEDIError = service.moveToPreviousError;
  const resetFocus = service.resetFocus;

  const moveToNextError = () => {
    track('webedi:next_error', { nextError: service.getCurrentError() });
    moveToNextWebEDIError();
  };

  const moveToPreviousError = () => {
    track('webedi:previous_error', { previousError: service.getPreviousError() });
    moveToPreviousWebEDIError();
  };

  if (numberOfErrors === 0) {
    return null;
  }
  return (
    <div className="flex gap-1">
      <Badge type="indicator" color={BadgeColor.red}>
        {mode === DisplayModes.view
          ? t('webedi:viewDocument.errorBanner.badge', { count: numberOfErrors, numberOfErrors })
          : t('webedi:editDocument.errorBanner.badge', { count: numberOfErrors, numberOfErrors })}
      </Badge>
      {numberOfErrors > 1 ? (
        <>
          <Button
            variant="secondary-danger"
            size="extra-small"
            onClick={moveToPreviousError}
            iconOnly
            LeftIcon={ChevronUpIcon}
            analyticsId="webedi:previous_error"
          />
          <Button
            variant="secondary-danger"
            size="extra-small"
            onClick={moveToNextError}
            iconOnly
            LeftIcon={ChevronDownIcon}
            analyticsId="webedi:next_error"
          />
        </>
      ) : (
        <Button
          variant="secondary-danger"
          size="extra-small"
          onClick={resetFocus}
          analyticsId="webedi:errors_focus_first"
        >
          {t('webedi:viewDocument.errorBanner.focusOnError')}
        </Button>
      )}
    </div>
  );
};

/**
 * i18n keys
 * t('webedi:viewDocument.errorBanner.badge', { count })
 * t('webedi:editDocument.errorBanner.badge', { count })
 * t('webedi:viewDocument.errorBanner.focusOnError')
 */
